import React from "react";
import { Link } from "react-router-dom";
import Footer from "@/components/layout/Footer";
import Navbar from "@/components/layout/Navbar";
import HeaderWithBackButton from "@/components/common/HeaderWithBackButton";
import SEOHead from "@/components/common/SEOHead";
import { SITE_URL } from "@/lib/constants/urls";
import { faqData, allFaqItems } from "@/pages/faq/faqData";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ = () => {
  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: allFaqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: `${SITE_URL}/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "FAQ",
          item: `${SITE_URL}/faq`,
        },
      ],
    },
  ];

  return (
    <>
      <SEOHead
        title="FAQ – AI Paper Writing & Review Questions | PaperVerse"
        description="Answers about PaperVerse: how the AI LaTeX writing assistant works, how AI reviews compare to peer review, AI disclosure and authorship, credits, and privacy for unpublished drafts."
        canonicalUrl={`${SITE_URL}/faq`}
        keywords="AI paper writing assistant FAQ, LaTeX AI editor, Overleaf alternative, AI research paper writer, AI paper review FAQ, disclose AI assistance journal, AI citation verification, paper review credits, unpublished paper privacy"
        structuredData={structuredData}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />

        <main className="flex-1 max-w-4xl mx-auto w-full px-4 py-12">
          <HeaderWithBackButton title="Frequently Asked Questions" />

          <p className="text-muted-foreground mb-8 max-w-2xl">
            Writing, reviewing and revising a paper on PaperVerse — how it works, what it
            costs, and where you stay in charge.
          </p>

          <nav aria-label="FAQ sections" className="flex flex-wrap gap-2 mb-12">
            {faqData.map((category) => (
              <a
                key={category.id}
                href={`#${category.id}`}
                className="text-sm px-3 py-1.5 rounded-full border border-border bg-secondary/40 text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors"
              >
                {category.category}
              </a>
            ))}
          </nav>

          {faqData.map((category) => (
            <section
              key={category.id}
              id={category.id}
              className="mb-12 scroll-mt-24"
              aria-labelledby={`${category.id}-heading`}
            >
              <h2
                id={`${category.id}-heading`}
                className="text-xl font-semibold mb-4 text-foreground"
              >
                {category.category}
              </h2>
              <Accordion
                type="single"
                collapsible
                className="w-full"
                defaultValue={category.id === "writer" ? `${category.id}-0` : undefined}
              >
                {category.items.map((item, index) => (
                  <AccordionItem key={item.q} value={`${category.id}-${index}`}>
                    <AccordionTrigger className="text-left">{item.q}</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-muted-foreground leading-relaxed">{item.a}</p>
                      {item.links && item.links.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
                          {item.links.map((link) => (
                            <Link
                              key={link.href}
                              to={link.href}
                              className="text-sm text-primary hover:underline"
                            >
                              {link.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          ))}

          <div className="mt-12 bg-secondary/50 p-6 rounded-lg">
            <h2 className="text-xl font-semibold mb-2">
              Couldn't find what you're looking for?
            </h2>
            <p>
              Contact our support team at{" "}
              <a href="mailto:contact@paperverse.io" className="text-primary">
                contact@paperverse.io
              </a>{" "}
              and we'll be happy to help.
            </p>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default FAQ;
